/* ============================================================
   SONDER · Klaviyo onsite: signup forms, welcome pop-up,
   back-in-stock and launch countdowns.
   Without a public key every form falls back to its native
   Shopify submission, so nothing breaks before Klaviyo is set up.
   ============================================================ */
(() => {
  "use strict";
  if (window.__sonderKlaviyo) return;
  window.__sonderKlaviyo = true;
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const CFG = Object.assign(
    { key: "", lists: {}, launch: "2026-11-01T09:00:00-08:00", pageType: "", customer: false, popup: {} },
    window.SonderKlaviyo || {}
  );
  const API = "https://a.klaviyo.com/client";
  const REVISION = "2026-07-15";
  const LAUNCH = new Date(CFG.launch);
  const store = {
    get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch (e) {} },
  };
  const session = {
    get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} },
  };

  // QA: ?phase=live or ?phase=pre previews either version of the copy
  const FORCED_PHASE = (location.search.match(/[?&]phase=(live|pre)/) || [])[1];
  const isLive = () => FORCED_PHASE ? FORCED_PHASE === "live" : isNaN(LAUNCH) || Date.now() >= LAUNCH.getTime();
  const phase = () => (isLive() ? "live" : "pre");
  const listFor = (name) => (CFG.lists || {})[name] || "";
  const enabled = () => !!CFG.key;
  const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || "").trim());
  const toE164 = (v) => {
    const d = String(v || "").replace(/\D/g, "");
    if (d.length === 10) return "+1" + d;
    if (d.length === 11 && d[0] === "1") return "+" + d;
    return "";
  };

  /* ---------- Klaviyo client API ---------- */
  async function post(path, body) {
    const res = await fetch(`${API}/${path}/?company_id=${encodeURIComponent(CFG.key)}`, {
      method: "POST",
      headers: { "Content-Type": "application/vnd.api+json", accept: "application/vnd.api+json", revision: REVISION },
      body: JSON.stringify(body),
    });
    if (!res.ok && res.status !== 202) throw new Error("klaviyo " + res.status);
    return res;
  }

  function subscribe({ email, phone, list, source, properties }) {
    const attributes = {};
    if (email) attributes.email = email;
    if (phone) attributes.phone_number = phone;
    if (properties) attributes.properties = properties;
    const subscriptions = {};
    if (email) subscriptions.email = { marketing: { consent: "SUBSCRIBED" } };
    if (phone) subscriptions.sms = { marketing: { consent: "SUBSCRIBED" } };
    attributes.subscriptions = subscriptions;
    return post("subscriptions", {
      data: {
        type: "subscription",
        attributes: { custom_source: source || "Website", profile: { data: { type: "profile", attributes } } },
        relationships: { list: { data: { type: "list", id: list } } },
      },
    });
  }

  function updateProfile(email, properties) {
    return post("profiles", { data: { type: "profile", attributes: { email, properties } } });
  }

  function identify(email, properties) {
    const k = window.klaviyo;
    if (k && typeof k.push === "function") k.push(["identify", Object.assign({ email }, properties || {})]);
  }

  function markSubscribed() { store.set("sonder-subscribed", "1"); }

  /* ---------- Phase-aware copy + countdowns ---------- */
  function applyPhase(root = document) {
    const p = phase();
    $$("[data-when]", root).forEach((el) => { el.hidden = el.dataset.when !== p; });
    const label = LAUNCH && !isNaN(LAUNCH)
      ? String(LAUNCH.getMonth() + 1).padStart(2, "0") + "." + String(LAUNCH.getDate()).padStart(2, "0")
      : "";
    $$("[data-launch-label]", root).forEach((el) => { if (label) el.textContent = label; });
  }

  function initCountdowns() {
    const boxes = $$("[data-countdown]");
    if (!boxes.length || isNaN(LAUNCH)) return;
    const pad = (n) => String(Math.max(0, n)).padStart(2, "0");
    const tick = () => {
      let t = Math.max(0, Math.floor((LAUNCH.getTime() - Date.now()) / 1000));
      const d = Math.floor(t / 86400); t -= d * 86400;
      const h = Math.floor(t / 3600); t -= h * 3600;
      const m = Math.floor(t / 60); const s = t - m * 60;
      boxes.forEach((b) => {
        const set = (k, v) => { const el = $(`[data-cd="${k}"]`, b); if (el) el.textContent = pad(v); };
        set("d", d); set("h", h); set("m", m); set("s", s);
      });
      if (d + h + m + s === 0) { applyPhase(); return; }
      setTimeout(tick, 1000);
    };
    tick();
  }

  /* ---------- Inline signup forms (footer, journal, password page) ----------
     <form data-klaviyo-form data-list="newsletter|early_access|auto" data-source="Footer"> */
  function initInlineForms() {
    $$("form[data-klaviyo-form]").forEach((form) => {
      form.addEventListener("submit", async (e) => {
        const input = $('input[type="email"]', form);
        const email = input ? input.value.trim() : "";
        let listName = form.dataset.list || "newsletter";
        if (listName === "auto") listName = isLive() ? "newsletter" : "early_access";
        const list = listFor(listName);
        if (!enabled() || !list) return; // native Shopify submit
        e.preventDefault();
        if (!emailOk(email)) { flash(form, "enter a valid email", true); return; }
        const btn = $('[type="submit"]', form);
        if (btn) btn.disabled = true;
        try {
          const properties = { signup_source: (form.dataset.source || "website").toLowerCase() };
          if (listName === "early_access") properties.early_access = true;
          await subscribe({ email, list, source: form.dataset.source || "Website", properties });
          identify(email);
          markSubscribed();
          flash(form, listName === "early_access" ? "you're on the list" : "you're in. check your inbox");
          form.reset();
        } catch (err) {
          flash(form, "something went wrong, try again", true);
        } finally {
          if (btn) btn.disabled = false;
        }
      });
    });
  }

  function flash(form, text, isErr) {
    let note = form.nextElementSibling && form.nextElementSibling.matches("[data-form-note]") ? form.nextElementSibling : null;
    if (!note) {
      note = document.createElement("p");
      note.setAttribute("data-form-note", "");
      note.setAttribute("role", "status");
      note.className = "form-note";
      form.insertAdjacentElement("afterend", note);
    }
    note.textContent = text;
    note.dataset.err = isErr ? "true" : "false";
  }

  /* ---------- Welcome / Early Access pop-up ---------- */
  function initPopup() {
    const pop = $("[data-spop]");
    const tab = $("[data-spop-tab]");
    const P = CFG.popup || {};
    if (!pop || P.enabled === false) return;
    applyPhase(pop);
    if (tab) applyPhase(tab);

    const blockedPage = ["cart", "password", "customers/login", "customers/register", "customers/account", "customers/order", "gift_card"].includes(CFG.pageType);
    const forced = /[?&]popup=1/.test(location.search);
    const subscribed = store.get("sonder-subscribed") === "1";
    const snoozedUntil = parseInt(store.get("sonder-pop-snooze") || "0", 10);
    const snoozed = Date.now() < snoozedUntil;
    const eligible = forced || (!subscribed && !CFG.customer);
    if (!eligible) return;

    let email = "";
    let lastFocus = null;
    const steps = $$("[data-spop-step]", pop);
    const go = (n) => {
      steps.forEach((s) => (s.hidden = s.dataset.spopStep !== String(n)));
      const target = $(`[data-spop-step="${n}"] input, [data-spop-step="${n}"] button:not([data-spop-close])`, pop)
        || $(`[data-spop-step="${n}"] .btn`, pop);
      if (target) setTimeout(() => target.focus({ preventScroll: true }), 50);
    };

    const open = () => {
      if (!pop.hidden) return;
      lastFocus = document.activeElement;
      pop.hidden = false;
      requestAnimationFrame(() => pop.classList.add("is-open"));
      document.documentElement.classList.add("spop-lock");
      if (tab) tab.hidden = true;
      session.set("sonder-pop-seen", "1");
      go(1);
    };
    const close = () => {
      if (pop.hidden) return;
      pop.classList.remove("is-open");
      document.documentElement.classList.remove("spop-lock");
      setTimeout(() => (pop.hidden = true), 300);
      const done = store.get("sonder-subscribed") === "1";
      if (!done) {
        store.set("sonder-pop-snooze", String(Date.now() + (P.snoozeDays || 7) * 864e5));
        if (tab && P.teaser !== false && !blockedPage) tab.hidden = false;
      }
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    };

    $$("[data-spop-close]", pop).forEach((b) => b.addEventListener("click", close));
    $$("[data-spop-next]", pop).forEach((b) => b.addEventListener("click", () => go(b.dataset.spopNext)));
    pop.addEventListener("keydown", (e) => {
      if (e.key === "Escape") close();
      if (e.key !== "Tab") return;
      const f = $$("button, input, a[href]", pop).filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    if (tab) tab.addEventListener("click", open);

    const showErr = (form, on) => { const el = $("[data-spop-err]", form); if (el) el.hidden = !on; };

    const emailForm = $("[data-spop-email]", pop);
    emailForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      const v = $('input[type="email"]', emailForm).value.trim();
      if (!emailOk(v)) { showErr(emailForm, true); return; }
      showErr(emailForm, false);
      email = v;
      const listName = isLive() ? "newsletter" : "early_access";
      const btn = $('[type="submit"]', emailForm);
      btn.disabled = true;
      try {
        if (enabled() && listFor(listName)) {
          await subscribe({ email, list: listFor(listName), source: "Popup", properties: { signup_source: "popup", early_access: listName === "early_access" } });
          identify(email);
        }
        markSubscribed();
        go(2);
      } catch (err) {
        showErr(emailForm, true);
        const el = $("[data-spop-err]", emailForm);
        if (el) el.textContent = "Something went wrong. Try again.";
      } finally {
        btn.disabled = false;
      }
    });

    $$("[data-spop-mode]", pop).forEach((b) =>
      b.addEventListener("click", () => {
        const mode = b.dataset.spopMode;
        if (enabled() && email) updateProfile(email, { mode }).catch(() => {});
        go(3);
      })
    );

    const smsForm = $("[data-spop-sms]", pop);
    smsForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      const phone = toE164($('input[type="tel"]', smsForm).value);
      if (!phone) { showErr(smsForm, true); return; }
      showErr(smsForm, false);
      const list = listFor("sms") || listFor(isLive() ? "newsletter" : "early_access");
      const btn = $('[type="submit"]', smsForm);
      btn.disabled = true;
      try {
        if (enabled() && list) await subscribe({ email, phone, list, source: "Popup SMS", properties: { signup_source: "popup" } });
        go(4);
      } catch (err) {
        showErr(smsForm, true);
      } finally {
        btn.disabled = false;
      }
    });

    if (forced) { open(); return; }
    if (session.get("sonder-pop-seen")) return;

    const mobile = matchMedia("(max-width: 700px)").matches;
    let fired = false;
    let timer = 0;
    const fire = () => { if (fired) return; fired = true; open(); cleanup(); };
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - innerHeight;
      if (h > 0 && scrollY / h > 0.4) fire();
    };
    const onLeave = (e) => { if (e.clientY <= 0 && !e.relatedTarget) fire(); };
    function cleanup() {
      clearTimeout(timer);
      removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseout", onLeave);
    }

    if (snoozed) { if (tab && P.teaser !== false && !blockedPage) tab.hidden = false; return; }

    // Exit intent: desktop, product and cart pages, for visitors who haven't seen the pop-up
    const useExit = P.exitIntent !== false && !mobile && (CFG.pageType === "product" || CFG.pageType === "cart");
    if (useExit) document.addEventListener("mouseout", onLeave);
    if (blockedPage) return;

    // Timed or scroll trigger, whichever comes first
    timer = setTimeout(fire, ((P.delay || 8) + (mobile ? 2 : 0)) * 1000);
    addEventListener("scroll", onScroll, { passive: true });
  }

  /* ---------- Back in stock ---------- */
  function initBackInStock() {
    // Swap add to cart for the restock form when a sold-out supply tile is chosen
    $$("[data-modes] .mode[data-available]").forEach((mode) =>
      mode.addEventListener("click", () => {
        const form = mode.closest("form");
        const box = form && form.parentNode ? $("[data-bis]", form.parentNode) : null;
        const btn = form && $('button[type="submit"].btn', form);
        const soldOut = mode.dataset.available === "false";
        if (box) { box.hidden = !soldOut; box.dataset.variant = mode.dataset.value; }
        if (btn) btn.disabled = soldOut;
      })
    );
    $$("[data-bis]").forEach((box) => {
      const form = $("form", box);
      if (!form) return;
      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        const email = $('input[type="email"]', form).value.trim();
        const variant = box.dataset.variant || ($("#buy-variant") || {}).value;
        if (!emailOk(email)) { flash(form, "enter a valid email", true); return; }
        if (!enabled()) { flash(form, "notifications open soon", true); return; }
        try {
          await post("back-in-stock-subscriptions", {
            data: {
              type: "back-in-stock-subscription",
              attributes: { channels: ["EMAIL"], profile: { data: { type: "profile", attributes: { email } } } },
              relationships: { variant: { data: { type: "catalog-variant", id: `$shopify:::$default:::${variant}` } } },
            },
          });
          flash(form, "we'll email you the moment it's back");
          form.reset();
        } catch (err) {
          flash(form, "something went wrong, try again", true);
        }
      });
    });
  }

  /* Added to Cart and Viewed Product come from the Klaviyo app embed's
     Shopify behavioral tracking, so no custom events are sent here. */

  function boot() {
    applyPhase();
    initCountdowns();
    initInlineForms();
    initPopup();
    initBackInStock();
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  window.SonderSignup = { subscribe, updateProfile, isLive };
})();
